import { useState, useEffect, useCallback } from "react";
import { ValidationResult, BalanceCheckResult } from "../types/validation";
import {
  useCheckWalletBalance,
  useTradeStatus,
  useValidationSummary,
  useReopenTrade,
} from "../graphql/validation";
import { logger } from "../services/logging";

export const usePositionValidation = (
  tradeId: number,
  quantity: number,
  price: number,
) => {
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [balanceResult, setBalanceResult] = useState<BalanceCheckResult | null>(
    null,
  );
  const [isTradeClosed, setIsTradeClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { checkWalletBalance } = useCheckWalletBalance();
  const { checkTradeStatus } = useTradeStatus();
  const { getValidationSummary } = useValidationSummary();

  // Check trade status on mount
  useEffect(() => {
    if (tradeId) {
      checkTradeStatus(tradeId).then((result) => {
        setIsTradeClosed(result.isClosed);
      });
    }
  }, [tradeId, checkTradeStatus]);

  // Check balance when quantity or price changes
  useEffect(() => {
    if (tradeId && quantity > 0 && price > 0) {
      checkBalance();
    } else {
      // For invalid inputs, still run basic validation
      updateValidation(null);
    }
  }, [tradeId, quantity, price]);

  // Update validation when trade status changes
  useEffect(() => {
    if (balanceResult || quantity <= 0 || price <= 0) {
      updateValidation(balanceResult);
    }
  }, [isTradeClosed, balanceResult, quantity, price]);

  const checkBalance = useCallback(async () => {
    if (!tradeId || quantity <= 0 || price <= 0) return;

    setIsLoading(true);
    try {
      const result = await checkWalletBalance(tradeId, quantity, price);
      setBalanceResult(result);

      // Update validation based on balance
      updateValidation(result);
    } catch (error) {
      logger.error(
        "Failed to check balance",
        { method: "checkBalance" },
        error,
      );
    } finally {
      setIsLoading(false);
    }
  }, [tradeId, quantity, price, checkWalletBalance]);

  const updateValidation = useCallback(
    (balance: BalanceCheckResult | null) => {
      const errors: string[] = [];
      const warnings: string[] = [];
      const suggestedActions: string[] = [];

      // Balance validation (only if balance result is available)
      if (balance && !balance.hasSufficientBalance) {
        errors.push("Insufficient wallet balance for this position");
        suggestedActions.push(
          `Deposit $${balance.suggestedDeposit} to proceed`,
        );
      }

      // Trade status validation
      if (isTradeClosed) {
        errors.push("Cannot modify positions in closed trades");
        suggestedActions.push("Reopen the trade to make changes");
      }

      // Basic validation
      if (quantity <= 0) {
        errors.push("Quantity must be greater than 0");
      }

      if (price <= 0) {
        errors.push("Price must be greater than 0");
      }

      // Warnings
      if (price > 1000000) {
        warnings.push("Price seems unusually high. Please verify the value.");
      }

      const newValidation: ValidationResult = {
        isValid: errors.length === 0,
        errors,
        warnings,
        suggestedActions,
      };

      setValidation(newValidation);
    },
    [quantity, price, isTradeClosed],
  );

  const refreshValidation = useCallback(async () => {
    if (tradeId) {
      try {
        const summary = await getValidationSummary(tradeId);
        setValidation(summary);
      } catch (error) {
        logger.error(
          "Failed to get validation summary",
          { method: "getValidationSummary" },
          error,
        );
      }
    }
  }, [tradeId, getValidationSummary]);

  const clearValidation = useCallback(() => {
    setValidation(null);
  }, []);

  return {
    validation,
    balanceResult,
    isTradeClosed,
    isLoading,
    checkBalance,
    refreshValidation,
    clearValidation,
    updateValidation,
  };
};

export const useTradeValidation = (tradeId: number) => {
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [isTradeClosed, setIsTradeClosed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { checkTradeStatus } = useTradeStatus();
  const { getValidationSummary } = useValidationSummary();
  const {
    reopenTrade: reopenTradeMutation,
    loading: reopenLoading,
    error: reopenError,
  } = useReopenTrade();

  useEffect(() => {
    if (tradeId) {
      checkTradeStatus(tradeId).then((result) => {
        setIsTradeClosed(result.isClosed);
      });
      // Also check validation on mount
      checkValidation();
    }
  }, [tradeId]);

  const checkValidation = useCallback(async () => {
    if (!tradeId) return;

    setIsLoading(true);
    try {
      const summary = await getValidationSummary(tradeId);
      setValidation(summary);
    } catch (error) {
      logger.error(
        "Failed to check validation",
        { method: "checkValidation" },
        error,
      );
    } finally {
      setIsLoading(false);
    }
  }, [tradeId, getValidationSummary]);

  const refreshValidation = useCallback(async () => {
    await checkValidation();
  }, [checkValidation]);

  const clearValidation = useCallback(() => {
    setValidation(null);
  }, []);

  const reopenTrade = useCallback(async () => {
    if (!tradeId) return;

    setIsLoading(true);
    try {
      // Use GraphQL mutation to reopen the trade
      const result = await reopenTradeMutation(tradeId);

      if (result) {
        // Update local state to reflect the trade is now open
        setIsTradeClosed(false);
        // Refresh validation
        await checkValidation();
      } else {
        throw new Error("Failed to reopen trade");
      }
    } catch (error) {
      logger.error("Failed to reopen trade", { method: "reopenTrade" }, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [tradeId, checkValidation, reopenTradeMutation]);

  return {
    validation,
    isTradeClosed,
    isLoading: isLoading || reopenLoading,
    checkValidation,
    refreshValidation,
    clearValidation,
    reopenTrade,
    reopenError,
  };
};

export const useWalletBalance = (
  tradeId: number,
  quantity: number,
  price: number,
) => {
  const [balanceResult, setBalanceResult] = useState<BalanceCheckResult | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);

  const { checkWalletBalance } = useCheckWalletBalance();

  // Check balance on mount and when inputs change
  useEffect(() => {
    if (tradeId && quantity > 0 && price > 0) {
      checkBalance();
    }
  }, [tradeId, quantity, price]);

  const checkBalance = useCallback(async () => {
    if (!tradeId || quantity <= 0 || price <= 0) return;

    setIsLoading(true);
    try {
      const result = await checkWalletBalance(tradeId, quantity, price);
      setBalanceResult(result);
    } catch (error) {
      logger.error(
        "Failed to check balance",
        { method: "checkBalance" },
        error,
      );
    } finally {
      setIsLoading(false);
    }
  }, [tradeId, quantity, price, checkWalletBalance]);

  const refreshBalance = useCallback(async () => {
    await checkBalance();
  }, [checkBalance]);

  const clearBalance = useCallback(() => {
    setBalanceResult(null);
  }, []);

  return {
    balanceResult,
    isLoading,
    checkBalance,
    refreshBalance,
    clearBalance,
  };
};
